import React from 'react';
import { Loader2 } from 'lucide-react';

const LoadingSpinner = ({
  size = 'md',
  color = 'blue',
  text,
  fullScreen = false,
  className = '',
  ...props
}) => {
  const sizes = {
    xs: 'w-4 h-4',
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
    '2xl': 'w-16 h-16',
  };

  const colors = {
    blue: 'text-blue-600',
    gray: 'text-gray-600',
    green: 'text-green-600',
    red: 'text-red-600',
    yellow: 'text-yellow-600',
    purple: 'text-purple-600',
    pink: 'text-pink-600',
    indigo: 'text-indigo-600',
    white: 'text-white',
  };

  const spinnerClasses = [
    sizes[size],
    colors[color],
    'animate-spin',
    className,
  ].filter(Boolean).join(' ');

  const Container = ({ children }) => {
    if (fullScreen) {
      return (
        <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center">
            {children}
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center" {...props}>
        {children}
      </div>
    );
  };

  return (
    <Container>
      <Loader2 className={spinnerClasses} />
      {text && (
        <span className="ml-2 text-gray-600 text-sm">
          {text}
        </span>
      )}
    </Container>
  );
};

// Inline spinner for buttons and small spaces
export const InlineSpinner = ({ size = 'sm', color = 'white', className = '' }) => {
  const sizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
  };

  const colors = {
    blue: 'text-blue-600',
    white: 'text-white',
    gray: 'text-gray-600',
  };

  return (
    <Loader2 className={`${sizes[size]} ${colors[color]} animate-spin ${className}`} />
  );
};

// Page loading component
export const PageLoading = ({ message = 'Loading...' }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <LoadingSpinner size="xl" color="blue" />
        <p className="mt-4 text-gray-600 text-lg">{message}</p>
      </div>
    </div>
  );
};

// Section loading component
export const SectionLoading = ({ message = 'Loading...', height = 'auto' }) => {
  const heightClasses = {
    auto: '',
    sm: 'h-32',
    md: 'h-48',
    lg: 'h-64',
    xl: 'h-80',
    screen: 'h-screen',
  };

  return (
    <div className={`flex items-center justify-center bg-gray-50 rounded-lg ${heightClasses[height]}`}>
      <div className="text-center p-8">
        <LoadingSpinner size="lg" color="blue" />
        <p className="mt-4 text-gray-600">{message}</p>
      </div>
    </div>
  );
};

// Card loading skeleton
export const CardSkeleton = ({ lines = 3, showAvatar = false }) => {
  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 animate-pulse">
      <div className="flex items-start space-x-4">
        {showAvatar && (
          <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
        )}
        <div className="flex-1 space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          {Array.from({ length: lines }).map((_, index) => (
            <div
              key={index}
              className={`h-3 bg-gray-200 rounded ${
                index === lines - 1 ? 'w-1/2' : 'w-full'
              }`}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Table loading skeleton
export const TableSkeleton = ({ rows = 5, columns = 4 }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: columns }).map((_, index) => (
            <div key={index} className="h-4 bg-gray-200 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
      <div className="divide-y divide-gray-200">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="p-4">
            <div className="grid grid-cols-4 gap-4">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <div key={colIndex} className="h-3 bg-gray-200 rounded animate-pulse"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LoadingSpinner;